import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import "./button.css";

export type ButtonVariant = "light" | "glass" | "dark";
export type ButtonIcon = "arrow" | "claude" | "none";

type Base = {
  /** light: on a photo (Book a call) · glass: on a photo (Why Claude) · dark: on white (Contact North). */
  variant: ButtonVariant;
  /** Drawn after the label. Without one, a light sweep crosses the button on hover. */
  icon?: ButtonIcon;
  className?: string;
  children: ReactNode;
};

type LinkButtonProps = Base & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof Base | "href"> & { href: string };
type NativeButtonProps = Base & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof Base> & { href?: undefined };
export type ButtonProps = LinkButtonProps | NativeButtonProps;

// The mockup's arrow-*.svg point up and are turned 90° on the page: here the turn is in the drawing,
// so CSS is free to move it.
function Arrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <g transform="rotate(90 8 8)">
        <path d="M8.00667 3.00667V13" />
        <path d="M4.01533 7.00933L8 2.99867L11.9847 7.00933" />
      </g>
    </svg>
  );
}

// claude-spark-white.svg, fill switched to currentColor (its clip-path id would repeat on a page of buttons).
function Spark() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M3.14293 10.637L6.28787 8.87227L6.34053 8.71847L6.28787 8.63347H6.13407L5.60787 8.60107L3.8108 8.55247L2.25247 8.48773L0.742733 8.4068L0.362267 8.3258L0.00606667 7.85633L0.0424667 7.62153L0.362267 7.407L0.8196 7.44753L1.83153 7.51633L3.34933 7.62153L4.45027 7.68633L6.08147 7.85633H6.34053L6.37693 7.75107L6.28787 7.68633L6.21907 7.62153L4.64867 6.55707L2.94867 5.4318L2.05827 4.7842L1.5766 4.45633L1.33373 4.14873L1.22853 3.47687L1.66567 2.9952L2.25253 3.03567L2.40227 3.07613L2.99727 3.53347L4.2682 4.51707L5.92773 5.73947L6.1706 5.9418L6.26773 5.873L6.27987 5.82447L6.17053 5.64227L5.26793 4.01113L4.3046 2.3516L3.8756 1.6636L3.76227 1.25067C3.7218 1.08067 3.69347 0.939067 3.69347 0.765L4.19133 0.089L4.46647 0L5.13027 0.0890667L5.4096 0.331867L5.8224 1.275L6.49027 2.76047L7.52647 4.7802L7.83 5.3792L7.99193 5.93373L8.0526 6.10373H8.15787V6.0066L8.24287 4.86927L8.40073 3.4728L8.55453 1.67567L8.60713 1.16973L8.85807 0.5626L9.35593 0.234733L9.74447 0.420933L10.0643 0.878267L10.0197 1.1738L9.82953 2.40827L9.45713 4.343L9.21427 5.63827H9.35593L9.51787 5.47633L10.1735 4.60613L11.2745 3.22993L11.7602 2.68353L12.3269 2.08047L12.6911 1.79307H13.3792L13.8852 2.54593L13.6585 3.32307L12.9502 4.2216L12.3633 4.98253L11.5214 6.11587L10.9952 7.02253L11.0438 7.0954L11.1693 7.0832L13.0716 6.67853L14.0997 6.49227L15.3261 6.2818L15.8806 6.54087L15.9413 6.80393L15.7227 7.34227L14.4114 7.66607L12.8733 7.97367L10.5823 8.51607L10.554 8.53633L10.5864 8.5768L11.6185 8.67393L12.0597 8.6982H13.1404L15.1521 8.848L15.6782 9.196L15.9939 9.62107L15.9413 9.94487L15.1318 10.3577L14.0389 10.0987L11.4889 9.49153L10.6147 9.27293H10.4933V9.3458L11.2219 10.0582L12.5575 11.2643L14.2292 12.8186L14.3142 13.2031L14.0997 13.5067L13.873 13.4743L12.4037 12.3693L11.8371 11.8715L10.554 10.7908H10.469V10.9041L10.7645 11.3372L12.3269 13.6848L12.4078 14.4053L12.2945 14.64L11.8897 14.7817L11.4445 14.7007L10.5297 13.4177L9.58667 11.9727L8.82573 10.6775L8.7326 10.7301L8.28327 15.5669L8.07287 15.8138L7.58713 16L7.1824 15.6924L6.96787 15.1945L7.1824 14.211L7.44147 12.9279L7.65193 11.9079L7.84213 10.641L7.95547 10.2201L7.9474 10.1917L7.85427 10.2039L6.89907 11.5153L5.446 13.4784L4.29647 14.7088L4.02127 14.8181L3.54367 14.5712L3.58813 14.13L3.85533 13.7374L5.446 11.7136L6.40527 10.4589L7.0246 9.7344L7.02047 9.62913H6.98407L2.7584 12.3734L2.00553 12.4705L1.68173 12.1669L1.72227 11.6691L1.87607 11.5072L3.147 10.6329L3.14293 10.637Z" />
    </svg>
  );
}

function Content({ icon, children }: { icon: ButtonIcon; children: ReactNode }) {
  return (
    <>
      {icon === "none" && <span className="ds-button__sheen" aria-hidden="true" />}
      <span className="ds-button__label">{children}</span>
      {icon === "arrow" && (
        // One arrow: on hover it nudges a few pixels to the right and bounces back.
        <span className="ds-button__icon ds-button__arrow" aria-hidden="true">
          <Arrow />
        </span>
      )}
      {icon === "claude" && (
        <span className="ds-button__icon ds-button__spark" aria-hidden="true">
          <Spark />
        </span>
      )}
    </>
  );
}

const classes = (variant: ButtonVariant, icon: ButtonIcon, className?: string) =>
  `ds-button ds-button--${variant} ds-button--icon-${icon}${className ? ` ${className}` : ""}`;

/** Call to action. Renders an <a> with `href`, a <button type="button"> without. */
export default function Button(props: ButtonProps) {
  if (typeof props.href === "string") {
    const { variant, icon = "none", className, children, ...link } = props;
    return (
      <a {...link} className={classes(variant, icon, className)}>
        <Content icon={icon}>{children}</Content>
      </a>
    );
  }
  const { variant, icon = "none", className, children, ...button } = props;
  return (
    <button type="button" {...button} className={classes(variant, icon, className)}>
      <Content icon={icon}>{children}</Content>
    </button>
  );
}
